import type { VariantProps } from '@gluestack-ui/nativewind-utils';
import React, { forwardRef, memo } from 'react';

import { headingStyle } from './styles';
type IHeadingProps = VariantProps<typeof headingStyle> &
  React.ComponentPropsWithoutRef<'h1'> & {
    as?: React.ElementType;
    testID?: string;
    numberOfLines?: number;
  };

const MappedHeading = memo(
  forwardRef<HTMLHeadingElement, IHeadingProps>(
    (
      {
        size,
        className,
        isTruncated,
        bold,
        underline,
        strikeThrough,
        sub,
        italic,
        highlight,
        testID,
        numberOfLines,
        ...props
      },
      ref
    ) => {
      const shouldTruncate = isTruncated || numberOfLines !== undefined;

      switch (size) {
        case '6xl':
        case '5xl':
        case '4xl':
        case '3xl':
        case 'h1-6xl':
        case 'h1-5xl':
        case 'h1-4xl':
        case 'h1-3xl':
        case 'h1-2xl':
        case 'h1-xl':
        case 'h1-lg':
        case 'h1-md':
        case 'h1-sm':
        case 'h1-xs':
        case 'h1-2xs':
          return (
            <h1
              className={headingStyle({
                size,
                isTruncated: shouldTruncate,
                bold,
                underline,
                strikeThrough,
                sub,
                italic,
                highlight,
                class: className,
              })}
              data-testid={testID}
              {...props}
              ref={ref}
            />
          );
        case '2xl':
        case 'h2-6xl':
        case 'h2-5xl':
        case 'h2-4xl':
        case 'h2-3xl':
        case 'h2-2xl':
        case 'h2-xl':
        case 'h2-lg':
        case 'h2-md':
        case 'h2-sm':
        case 'h2-xs':
        case 'h2-2xs':
          return (
            <h2
              className={headingStyle({
                size,
                isTruncated: shouldTruncate,
                bold,
                underline,
                strikeThrough,
                sub,
                italic,
                highlight,
                class: className,
              })}
              data-testid={testID}
              {...props}
              ref={ref}
            />
          );
        case 'xl':
        case 'h3-6xl':
        case 'h3-5xl':
        case 'h3-4xl':
        case 'h3-3xl':
        case 'h3-2xl':
        case 'h3-xl':
        case 'h3-lg':
        case 'h3-md':
        case 'h3-sm':
        case 'h3-xs':
        case 'h3-2xs':
          return (
            <h3
              className={headingStyle({
                size,
                isTruncated: shouldTruncate,
                bold,
                underline,
                strikeThrough,
                sub,
                italic,
                highlight,
                class: className,
              })}
              data-testid={testID}
              {...props}
              ref={ref}
            />
          );
        case 'lg':
        case 'h4-6xl':
        case 'h4-5xl':
        case 'h4-4xl':
        case 'h4-3xl':
        case 'h4-2xl':
        case 'h4-xl':
        case 'h4-lg':
        case 'h4-md':
        case 'h4-sm':
        case 'h4-xs':
        case 'h4-2xs':
          return (
            <h4
              className={headingStyle({
                size,
                isTruncated: shouldTruncate,
                bold,
                underline,
                strikeThrough,
                sub,
                italic,
                highlight,
                class: className,
              })}
              data-testid={testID}
              {...props}
              ref={ref}
            />
          );
        case 'md':
        case 'h5-6xl':
        case 'h5-5xl':
        case 'h5-4xl':
        case 'h5-3xl':
        case 'h5-2xl':
        case 'h5-xl':
        case 'h5-lg':
        case 'h5-md':
        case 'h5-sm':
        case 'h5-xs':
        case 'h5-2xs':
          return (
            <h5
              className={headingStyle({
                size,
                isTruncated: shouldTruncate,
                bold,
                underline,
                strikeThrough,
                sub,
                italic,
                highlight,
                class: className,
              })}
              data-testid={testID}
              {...props}
              ref={ref}
            />
          );
        case 'sm':
        case 'xs':
        case '2xs':
        case 'h6-6xl':
        case 'h6-5xl':
        case 'h6-4xl':
        case 'h6-3xl':
        case 'h6-2xl':
        case 'h6-xl':
        case 'h6-lg':
        case 'h6-md':
        case 'h6-sm':
        case 'h6-xs':
        case 'h6-2xs':
          return (
            <h6
              className={headingStyle({
                size,
                isTruncated: shouldTruncate,
                bold,
                underline,
                strikeThrough,
                sub,
                italic,
                highlight,
                class: className,
              })}
              data-testid={testID}
              {...props}
              ref={ref}
            />
          );
        default:
          return (
            <h5
              className={headingStyle({
                size,
                isTruncated: shouldTruncate,
                bold,
                underline,
                strikeThrough,
                sub,
                italic,
                highlight,
                class: className,
              })}
              data-testid={testID}
              {...props}
              ref={ref}
            />
          );
      }
    }
  )
);

const Heading = memo(
  forwardRef<HTMLHeadingElement, IHeadingProps>(
    ({ className, size = 'lg', as: AsComp, ...props }, ref) => {
      const { isTruncated, bold, underline, strikeThrough, sub, italic, highlight } = props;

      if (AsComp) {
        return (
          <AsComp
            className={headingStyle({
              size,
              isTruncated,
              bold,
              underline,
              strikeThrough,
              sub,
              italic,
              highlight,
              class: className,
            })}
            {...props}
            ref={ref}
          />
        );
      }

      return <MappedHeading className={className} size={size} ref={ref} {...props} />;
    }
  )
);

Heading.displayName = 'Heading';

export { Heading };
